import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Clock, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';

/**
 * LatestCallsCarousel - Displays latest calls in an elegant carousel
 * Shows 3 calls at a time, 9 total, auto-advances
 */
const LatestCallsCarousel = () => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const CALLS_PER_PAGE = 3;
  const MAX_CALLS = 9;

  useEffect(() => {
    fetchLatestCalls();
  }, []);

  const fetchLatestCalls = async () => {
    try {
      const response = await api.get(`/api/calls?limit=${MAX_CALLS}&sort=created_at&order=desc`);
      setCalls(response.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch latest calls:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carousel navigation
  const totalPages = Math.ceil(calls.length / CALLS_PER_PAGE);
  
  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  }, [totalPages]);

  // Auto-advance carousel every 6 seconds
  useEffect(() => {
    if (calls.length <= CALLS_PER_PAGE) return;
    
    const interval = setInterval(nextSlide, 6000);
    return () => clearInterval(interval);
  }, [calls.length, nextSlide]);

  // Get current visible calls
  const visibleCalls = calls.slice(
    currentIndex * CALLS_PER_PAGE,
    currentIndex * CALLS_PER_PAGE + CALLS_PER_PAGE
  );

  const getQualisColor = (qualis) => {
    const colors = {
      'A1': 'bg-emerald-500 text-white',
      'A2': 'bg-green-500 text-white',
      'A3': 'bg-lime-500 text-white',
      'A4': 'bg-yellow-500 text-white',
      'B1': 'bg-amber-500 text-white',
      'B2': 'bg-orange-500 text-white',
      'B3': 'bg-red-400 text-white',
      'B4': 'bg-red-500 text-white',
    };
    return colors[qualis] || 'bg-gray-500 text-white';
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  const getDaysUntil = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-[#002FA7] border-t-transparent rounded-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (calls.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-3">
            Chamadas Recentes
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Confira as últimas oportunidades de publicação cadastradas na plataforma
          </p>
        </div>

        {/* Carousel container */}
        <div className="relative">
          {/* Navigation arrows */}
          {calls.length > CALLS_PER_PAGE && (
            <>
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors border border-gray-200 hidden md:flex items-center justify-center"
              >
                <ChevronLeft className="h-6 w-6 text-[#002FA7]" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-gray-50 transition-colors border border-gray-200 hidden md:flex items-center justify-center"
              >
                <ChevronRight className="h-6 w-6 text-[#002FA7]" />
              </button>
            </>
          )}

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-500">
            {visibleCalls.map((call) => {
              const daysUntil = getDaysUntil(call.deadline);
              const isUrgent = daysUntil !== null && daysUntil <= 7 && daysUntil > 0;
              
              return (
                <div
                  key={call.call_id}
                  onClick={() => navigate(`/chamada/${call.slug || call.call_id}`)}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl hover:border-[#002FA7]/20 transition-all duration-300 cursor-pointer group"
                >
                  {/* Card header with gradient */}
                  <div className="bg-gradient-to-r from-[#002FA7] to-[#0040D0] p-4">
                    <div className="flex items-start justify-between gap-2">
                      <Badge className={`${getQualisColor(call.qualis_classification)} text-xs font-bold`}>
                        QUALIS {call.qualis_classification}
                      </Badge>
                      {call.continuous_flow ? (
                        <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-xs">
                          <Clock className="h-3 w-3 mr-1" />
                          Fluxo Contínuo
                        </Badge>
                      ) : isUrgent ? (
                        <Badge className="bg-red-500 text-white text-xs animate-pulse">
                          {daysUntil} dias
                        </Badge>
                      ) : null}
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="p-5">
                    <h3 className="font-bold text-lg text-[#0A0A0A] mb-2 line-clamp-2 group-hover:text-[#002FA7] transition-colors">
                      {call.title}
                    </h3>
                    
                    <p className="text-sm text-gray-600 mb-3 flex items-center gap-2">
                      <BookOpen className="h-4 w-4 flex-shrink-0" />
                      <span className="line-clamp-1">{call.journal_name}</span>
                    </p>

                    {/* Deadline */}
                    {!call.continuous_flow && call.deadline && (
                      <div className={`flex items-center gap-2 text-sm ${isUrgent ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                        <Calendar className="h-4 w-4" />
                        <span>Prazo: {formatDeadline(call.deadline)}</span>
                      </div>
                    )}

                    {/* Areas */}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {call.knowledge_areas?.slice(0, 2).map((area, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          {area.length > 20 ? area.substring(0, 20) + '...' : area}
                        </span>
                      ))}
                      {call.knowledge_areas?.length > 2 && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          +{call.knowledge_areas.length - 2}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card footer */}
                  <div className="px-5 pb-4">
                    <div className="flex items-center justify-between text-sm text-[#002FA7] font-medium group-hover:text-[#001f75]">
                      <span>Ver detalhes</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Dots indicator */}
          {calls.length > CALLS_PER_PAGE && (
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2.5 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-8 bg-[#002FA7]' 
                      : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Mobile navigation */}
          {calls.length > CALLS_PER_PAGE && (
            <div className="flex justify-center gap-4 mt-6 md:hidden">
              <Button
                variant="outline"
                size="icon"
                onClick={prevSlide}
                className="rounded-full"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={nextSlide}
                className="rounded-full"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <div className="text-center mt-10">
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-[#002FA7] hover:bg-[#001f75] text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            Ver todas as chamadas
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LatestCallsCarousel;
